import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { KebutuhanKhusus } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { Switch } from "@/components/ui/switch"

const postData = async (newData: KebutuhanKhusus) => {
  const response = await api.post("/master/kebutuhan-khusus/store", newData)
  return response.data
}

const patchData = async (newData: KebutuhanKhusus) => {
  const response = await api.patch("/master/kebutuhan-khusus/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_kebutuhan_khusus: number }) => {
  const response = await api({
    url: "/master/kebutuhan-khusus/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: KebutuhanKhusus | null
  closeModal: () => void
}) {
  const [form, setForm] = useState({
    id_ref_kebutuhan_khusus: selectedRow?.id_ref_kebutuhan_khusus || 0,
    kode: selectedRow?.kode || "",
    nama_kebutuhan_khusus: selectedRow?.nama_kebutuhan_khusus || "",
    id_feeder: selectedRow?.id_feeder || "",
    is_aktif: selectedRow?.is_aktif ?? true,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listKebutuhanKhusus"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Kebutuhan Khusus"
          description={`Apakah anda yakin akan menghapus data kebutuhan khusus "${selectedRow?.nama_kebutuhan_khusus}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost
            ? "Insert Data Kebutuhan Khusus"
            : "Update Data Kebutuhan Khusus"
        }
      />

      <div>
        <Label>Kode</Label>
        <Input
          required
          placeholder="Ketik kode kebutuhan khusus..."
          value={form.kode}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              kode: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Nama Kebutuhan Khusus</Label>
        <Input
          required
          placeholder="Ketik nama kebutuhan khusus..."
          value={form.nama_kebutuhan_khusus}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              nama_kebutuhan_khusus: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Id Feeder</Label>
        <Input
          placeholder="Ketik id feeder..."
          value={form.id_feeder}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              id_feeder: event.target.value,
            }))
          }
        />
      </div>

      <div className="flex gap-3">
        <Switch
          id="status"
          checked={form.is_aktif}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_aktif: !prev.is_aktif,
            }))
          }
        />
        <Label htmlFor="status">Status Nonaktif/Aktif</Label>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
